import React, { useEffect, useState, useRef } from 'react';
import { getSuggestedStoryInputs, generateStory } from '../../api/storyApi';
import { useTranslation } from 'react-i18next';
import './CreateStory.css';
import { Link } from 'react-router-dom';
import { useSectionVisibility } from '../../hooks/useSectionVisibility';

const CreateStory = ({ onSuccess, onFinish, onLoaded, onVisible }) => {
    const [userInput, setUserInput] = useState('');
    const [elements, setElements] = useState([]);
    const [aiSuggestions, setAiSuggestions] = useState([]);
    const [error, setError] = useState('');
    const [inviteUpgradeSubscription, setInviteUpgradeSubscription] = useState(false);
    const { t } = useTranslation();
    const firstLoad = useRef(true);
    const ref = useSectionVisibility('create-story', onVisible);
    
    
    useEffect(() => {
        const fetchSuggestions = async () => {
            try {
                const response = await getSuggestedStoryInputs();
                if (response.success) {
                    setAiSuggestions(response.suggestion.suggestions);                
                }
    
            } catch (error) {
                setError('createStory.cannotGetSuggestion');
            } finally {
                if (firstLoad.current) {
                    onLoaded(3); // sectionId for CreateStory = 3
                    firstLoad.current = false;
                }
            }
        };
        
        setInviteUpgradeSubscription(false);
        if (aiSuggestions.length === 0) {
            fetchSuggestions();
        }
    }, [aiSuggestions, onLoaded]);

    const addElement = (value) => {
        if (!value.trim()) return;
        setElements((prev) => [...prev, value]);
        setUserInput('');
    };

    const removeElement = (value) => {
        setElements((prev) => prev.filter((item) => item !== value));
    };

    const handleAISelect = (value) => addElement(value);

    const handleCreateNewStory = async () => {
        try {
            setError(null);
            setInviteUpgradeSubscription(false);
            const generateStoryResult = await generateStory(elements);
            if (generateStoryResult.success) {
                const newStory = (({ _id, title, coverImage, generatingStatus, isPublic, date }) => ({ _id, title, coverImage, generatingStatus, isPublic, date }))(generateStoryResult.story);
                onSuccess(newStory); // pollStoryStatus(newStory._id);
            }
            onFinish();
            setElements([]);
            setAiSuggestions([]);
        } catch (error) {
            if (error.response?.status === 403) {
                setError('createStory.limitReached');
                setInviteUpgradeSubscription(true);
            } else {
                setError('createStory.errorCreatingStory');
            }
        }
    };

    return (
        <section id="create-story" ref={ref} className="story-section" data-bg="url('/images/bg_create_story.png')" data-filter="brightness(0.5)">
            <h2 className='story-section-h2'>🪄 {t('browseStories.createStory')} 🪄</h2>
            <div className="create-story-container">
                <h2>{t('createStory.whatIsStoryAbout')}</h2>
                <div className="input-section">
                    <input
                        type="text"
                        placeholder={t('createStory.addYourIdea')}
                        value={userInput}
                        onChange={(e) => setUserInput(e.target.value)}
                    />
                    <button onClick={() => addElement(userInput)}>+</button>
                </div>
                <div className="ai-suggestions">
                    <h3>{t('createStory.suggestions')}</h3>
                    {aiSuggestions && aiSuggestions.map((value, index) => (
                        <button key={index} className="chip" onClick={() => handleAISelect(value)}>
                            {value}
                        </button>
                    ))}
                </div>
                <div className="added-elements">
                    {elements.length > 0 && <h4>{t('createStory.yourStoryElements')}</h4>}
                    {elements.map((item, index) => (
                        <span key={index} className="added-chip">
                            {item}
                            <button className='added-chip-remove' onClick={() => removeElement(item)}>✕</button>
                        </span>
                    ))}
                </div>
                <div className="actions">
                    <button className="submit-button" onClick={handleCreateNewStory}>
                        {t('createStory.letsCreate')}
                    </button>
                    <button className="cancel-button" onClick={onFinish}>
                        {t('createStory.cancel')}
                    </button>
                </div>
                {error && <p className='form-error'>{t(error)}</p>}
                {inviteUpgradeSubscription &&<Link to="/profile" className='message-link'>{t('createStory.upgradeSubscription')}</Link>}
            </div>
        </section>
    );
};

export default CreateStory;
